import { useState } from 'react'

import { Banner } from '@/components/ui/banner'
import { Button } from '@/components/ui/button'
import { DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Field, FieldDescription, FieldError, FieldGroup, FieldLabel } from '@/components/ui/field'
import { Input } from '@/components/ui/input'
import { PhoneInput } from '@/components/ui/phoneInput'
import { usePhoneField } from '@/hooks/usePhoneField'

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

/**
 * The "Get quotes for free" dialog's only contact ask, landing after the visitor
 * has already seen their estimates. Submitting it usually signs them straight
 * in; the dialog falls through to a code only when the address already has an
 * account.
 */
export function EmailCaptureStep({
  initialEmail = '',
  initialPhone = '',
  isSending,
  errorMessage,
  onSubmit,
}: {
  initialEmail?: string
  initialPhone?: string
  isSending: boolean
  errorMessage?: string
  onSubmit: (contact: { email: string; phone: string }) => void
}) {
  const [email, setEmail] = useState(initialEmail)
  const [phone, setPhone] = useState(initialPhone)
  const isEmailValid = EMAIL_REGEX.test(email.trim())
  const phoneField = usePhoneField()

  function handleSubmit(event: React.FormEvent) {
    event.preventDefault()
    if (!isEmailValid || isSending) return
    if (!phoneField.check(phone)) return
    onSubmit({ email: email.trim(), phone: phone.trim() })
  }

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-5">
      <DialogHeader>
        <DialogTitle>Where should we send your quotes?</DialogTitle>
        <DialogDescription>We&apos;ll start your application and send your quotes here.</DialogDescription>
      </DialogHeader>

      <FieldGroup className="gap-4">
        <Field data-invalid={errorMessage ? true : undefined}>
          <FieldLabel htmlFor="verify-email">Work email address</FieldLabel>
          <Input
            id="verify-email"
            type="email"
            inputMode="email"
            autoComplete="email"
            autoFocus
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={isSending}
            aria-invalid={errorMessage ? true : undefined}
          />
        </Field>
        <Field>
          <FieldLabel htmlFor="verify-phone" optional>
            Phone number
          </FieldLabel>
          <PhoneInput
            id="verify-phone"
            autoComplete="tel"
            value={phone}
            onChange={(v) => setPhone(v || '')}
            disabled={isSending}
            error={!!phoneField.error}
          />
          <FieldError>{phoneField.error}</FieldError>
          <FieldDescription>
            By submitting, you agree Cai may contact you about your insurance needs. See our{' '}
            <a href="/privacy" target="_blank" rel="noopener noreferrer">
              Privacy Policy
            </a>
            .
          </FieldDescription>
        </Field>
      </FieldGroup>

      {errorMessage && (
        <Banner variant="error" small>
          {errorMessage}
        </Banner>
      )}

      <Button
        type="submit"
        className="w-full"
        disabled={isSending || !isEmailValid}
        loading={isSending ? 'Setting up your account…' : undefined}
      >
        Continue
      </Button>
    </form>
  )
}
